import { ConsumptionResponse } from '../models/ConsumptionResponse';
import { ConsumptionUnitEnum } from '../enum/ConsumptionUnitEnum';
import { DateTime } from 'luxon';
import { PeriodEnum } from '../enum/PeriodEnum';
import { VolumeTypeEnum } from '../enum/VolumeTypeEnum';
import axiosBearerClient from '../axiosBearerClient';
import { useQuery } from '@tanstack/react-query';

export const useGetConsumptions = ({
  sdpId,
  unit,
  period,
  volumesType,
  startDate,
  endDate,
  meterLastUpdate,
  enabled,
}: {
  sdpId?: string;
  unit: ConsumptionUnitEnum;
  period: PeriodEnum | null;
  volumesType: VolumeTypeEnum[];
  startDate?: string | null;
  endDate?: string | null;
  meterLastUpdate?: Date | string | null;
  enabled?: boolean;
}) => {
  const { isFetching, error, data, isError, refetch } = useQuery({
    queryKey: ['consumptions', sdpId, period, volumesType, unit, startDate, endDate, enabled],
    enabled: !!sdpId && !!period && !!volumesType && !!startDate && !!endDate && enabled,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: false,
    queryFn: async () => {
      const newStartDate = startDate
        ? DateTime.fromISO(startDate!)
            .setZone('Europe/Brussels')
            .startOf('day')
            .toJSDate()
            .toISOString()
        : startDate;
      const newEndDate = endDate
        ? DateTime.fromISO(endDate!)
            .setZone('Europe/Brussels')
            .endOf('day')
            .toJSDate()
            .toISOString()
        : endDate;

      return await axiosBearerClient.get<ConsumptionResponse[]>(
        `/${
          unit === ConsumptionUnitEnum.EURO ? 'tariffs' : 'consumptions'
        }/${sdpId}?period=${period}&volumeType=${volumesType.join(',')}&meterLastUpdate=${
          meterLastUpdate ? new Date(meterLastUpdate).toISOString() : new Date().toISOString()
        }${startDate ? `&startDate=${newStartDate}` : ''}${endDate ? `&endDate=${newEndDate}` : ''}`
      );
    },
  });

  return {
    isFetching,
    error,
    isError,
    data,
    refetch,
  };
};

export const useGetConsumptionsBoundaries = ({
  sdpId,
  unit,
  period,
}: {
  sdpId?: string;
  unit: ConsumptionUnitEnum;
  period: PeriodEnum | null;
}) => {
  const { isFetching, error, data, isError, refetch } = useQuery({
    queryKey: ['consumptions-boundaries', sdpId, period, unit],
    enabled: !!sdpId && !!period && !!unit,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: false,
    queryFn: async () => {
      return await axiosBearerClient.get<{
        startDate: string;
        endDate: string;
      }>(
        `/${
          unit === ConsumptionUnitEnum.EURO ? 'tariffs' : 'consumptions'
        }/${sdpId}/boundaries?period=${period}`
      );
    },
  });

  return {
    isFetching,
    error,
    isError,
    data,
    refetch,
  };
};
